/**
 * II-Agent UI Module
 * Handles all UI interactions for the II-Agent page
 * Sprint 32: OOA/OOD Refactoring - Orchestrator pattern using extracted modules
 *
 * @module IIAgentUI
 */

// Import types from centralized types module
import {
    InfrastructureReport,
    Anomaly,
    SecurityFinding,
    CostRecommendation,
    ServiceMap,
    ViewType,
    ClaudeStatus,
    DocumentSource
} from './types/ii-agent-types';

// Import extracted modules
import { ChatManager } from './managers/chat-manager';
import { HistoryManager } from './managers/history-manager';
import { UIStateManager } from './managers/ui-state-manager';
import { ServiceMapRenderer } from './renderers/service-map-renderer';
import { AnalysisRenderer } from './renderers/analysis-renderer';
import { ClaudeService } from './services/claude-service';
import { ContextService } from './services/context-service';
import { safeFeatherReplace } from './utils/html-utils';

// Sprint 28: Import Chat History Service for type definitions
import { ChatHistoryEntry, ChatHistoryMessage } from '../services/chat-history-service';

// Sprint 31: Import environment configuration
import { environment, isMainnet } from '../config/environment';

/**
 * II-Agent UI class
 * Refactored to use OOA/OOD principles - acts as an orchestrator
 */
export class IIAgentUI {
    // Managers (OOA/OOD extracted modules)
    private chatManager: ChatManager;
    private historyManager: HistoryManager;
    private uiStateManager: UIStateManager;

    // Renderers
    private serviceMapRenderer: ServiceMapRenderer;
    private analysisRenderer: AnalysisRenderer;

    // Services
    private claudeService: ClaudeService;
    private contextService: ContextService;

    // State
    private listenersSetup: boolean = false;
    private dataLoaded: boolean = false;
    private initializationComplete: boolean = false;
    private conversationId: string | null = null;
    private settingsService: any = null;
    private claudeStatus: ClaudeStatus | null = null;
    private currentChatId: string | null = null;
    private autoSaveEnabled: boolean = true;

    // Sprint 39: File attachment state
    private attachedFile: {
        filePath: string;
        fileName: string;
        fileSize: number;
        fileType: string;
    } | null = null;

    // LocalStorage key for API key
    private readonly API_KEY_STORAGE_KEY = 'ada_claude_api_key';

    /**
     * Constructor - initialize all modules
     */
    constructor() {
        // Initialize managers
        this.chatManager = new ChatManager('chat-messages');
        this.historyManager = new HistoryManager('history-list', (entry) => this.loadHistoryEntry(entry.id));
        this.uiStateManager = new UIStateManager((view) => {
            if (view === 'history') {
                this.historyManager.load();
            }
        });

        // Initialize renderers
        this.serviceMapRenderer = new ServiceMapRenderer();
        this.analysisRenderer = new AnalysisRenderer();

        // Initialize services
        this.claudeService = new ClaudeService();
        this.contextService = new ContextService();
    }

    /**
     * Initialize the II-Agent UI
     */
    initialize(): void {
        // Get settings service
        if ((window as any).SettingsService) {
            this.settingsService = new (window as any).SettingsService();
            console.log('IIAgentUI: SettingsService created, aiBackend =', this.settingsService.getAiBackend());
        } else {
            console.warn('IIAgentUI: SettingsService not found on window');
        }

        this.setupEventListeners();
        this.loadInitialData();
        this.checkClaudeStatus();
        this.checkHeaderConversation();
    }

    /**
     * Check for conversation transferred from header Ask Ada
     */
    private checkHeaderConversation(): void {
        try {
            const stored = sessionStorage.getItem('headerAdaConversation');
            if (!stored) return;

            const conversation = JSON.parse(stored);
            const age = Date.now() - conversation.timestamp;
            if (age > 30000) {
                sessionStorage.removeItem('headerAdaConversation');
                return;
            }

            if (conversation.question && conversation.answer) {
                setTimeout(() => {
                    this.loadConversationFromHeader(conversation.question, conversation.answer);
                }, 200);
            }

            sessionStorage.removeItem('headerAdaConversation');
        } catch (error) {
            console.error('IIAgentUI: Error checking header conversation:', error);
            sessionStorage.removeItem('headerAdaConversation');
        }
    }

    /**
     * Check Claude API status and update the backend indicator
     */
    private async checkClaudeStatus(): Promise<void> {
        try {
            const canisterClient = (window as any).canisterClient;
            const isConfigured = canisterClient ? await canisterClient.isClaudeConfigured() : false;

            this.claudeStatus = {
                configured: isConfigured,
                enabled: isConfigured,
                model: 'claude-3-5-sonnet-20241022',
                requests_today: 0
            };

            this.updateBackendIndicator();
        } catch (error) {
            console.warn('IIAgentUI: Failed to check Claude status:', error);
            this.claudeStatus = {
                configured: false,
                enabled: false,
                model: 'unknown',
                requests_today: 0
            };
            this.updateBackendIndicator();
        }
    }

    /**
     * Update the backend status indicator
     */
    private updateBackendIndicator(): void {
        const freshSettings = (window as any).SettingsService ? new (window as any).SettingsService() : null;
        const aiBackend = freshSettings?.getAiBackend() || 'local';
        this.uiStateManager.updateBackendIndicator(this.claudeStatus, aiBackend);
    }

    /**
     * Refresh settings service
     */
    public refreshSettings(): void {
        if ((window as any).SettingsService) {
            this.settingsService = new (window as any).SettingsService();
            console.log('IIAgentUI: Settings refreshed, aiBackend =', this.settingsService.getAiBackend());
        }
    }

    /**
     * Setup event listeners
     */
    private setupEventListeners(): void {
        if (this.listenersSetup) {
            console.log('Event listeners already setup, skipping');
            return;
        }

        // Analysis buttons
        this.setupAnalysisButtons();

        // Service Map buttons
        this.setupServiceMapButtons();

        // Chat interface
        this.setupChatInterface();

        // View switchers
        document.querySelectorAll('[data-view]').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const target = e.target as HTMLElement;
                const view = target.dataset.view as ViewType;
                if (view) {
                    this.switchView(view);
                }
            });
        });

        this.listenersSetup = true;

        // Clear chat input
        const chatInputClear = document.getElementById('chat-input') as HTMLInputElement;
        if (chatInputClear) {
            chatInputClear.value = '';
        }

        setTimeout(() => {
            this.initializationComplete = true;
            console.log('II-Agent UI initialization complete');
        }, 500);
    }

    /**
     * Setup analysis button listeners
     */
    private setupAnalysisButtons(): void {
        const btnAnalyze = document.getElementById('btn-analyze-infrastructure');
        const btnAnomalies = document.getElementById('btn-detect-anomalies');
        const btnSecurity = document.getElementById('btn-audit-security');
        const btnCost = document.getElementById('btn-cost-optimization');

        if (btnAnalyze) btnAnalyze.addEventListener('click', () => this.runInfrastructureAnalysis());
        if (btnAnomalies) btnAnomalies.addEventListener('click', () => this.runAnomalyDetection());
        if (btnSecurity) btnSecurity.addEventListener('click', () => this.runSecurityAudit());
        if (btnCost) btnCost.addEventListener('click', () => this.runCostOptimization());

        // Anomaly threshold slider
        const anomalyThreshold = document.getElementById('anomaly-threshold') as HTMLInputElement;
        if (anomalyThreshold) {
            anomalyThreshold.addEventListener('input', (e) => {
                const valueDisplay = document.getElementById('anomaly-threshold-value');
                if (valueDisplay) {
                    valueDisplay.textContent = (e.target as HTMLInputElement).value;
                }
            });
        }
    }

    /**
     * Setup service map button listeners
     */
    private setupServiceMapButtons(): void {
        const btnServiceMap = document.getElementById('btn-generate-service-map');
        if (btnServiceMap) {
            btnServiceMap.addEventListener('click', () => {
                const modal = document.getElementById('service-map-modal');
                if (modal) modal.classList.remove('hidden');
                if (typeof (window as any).runServiceDiscovery === 'function') {
                    (window as any).runServiceDiscovery();
                }
            });
        }

        const serviceMapPrompt = document.getElementById('service-map-prompt') as HTMLInputElement;
        if (serviceMapPrompt) {
            serviceMapPrompt.addEventListener('keypress', (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    this.generateServiceMap();
                }
            });
        }

        // Log viewer buttons
        const btnShowLog = document.getElementById('btn-show-service-log');
        const btnCloseLogModal = document.getElementById('btn-close-log-modal');
        const btnCloseLogModalFooter = document.getElementById('btn-close-log-modal-footer');
        const btnCopyLog = document.getElementById('btn-copy-log');

        if (btnShowLog) btnShowLog.addEventListener('click', () => this.serviceMapRenderer.showLog());
        if (btnCloseLogModal) btnCloseLogModal.addEventListener('click', () => this.serviceMapRenderer.closeLog());
        if (btnCloseLogModalFooter) btnCloseLogModalFooter.addEventListener('click', () => this.serviceMapRenderer.closeLog());
        if (btnCopyLog) btnCopyLog.addEventListener('click', () => this.serviceMapRenderer.copyLogToClipboard());

        const logModal = document.getElementById('service-log-modal');
        if (logModal) {
            logModal.addEventListener('click', (e) => {
                if (e.target === logModal) this.serviceMapRenderer.closeLog();
            });
        }
    }

    /**
     * Setup chat interface listeners
     */
    private setupChatInterface(): void {
        const btnSendChat = document.getElementById('btn-send-chat');
        if (btnSendChat) btnSendChat.addEventListener('click', () => this.sendChatMessage());

        const btnSaveChat = document.getElementById('btn-save-chat');
        if (btnSaveChat) btnSaveChat.addEventListener('click', () => this.chatManager.exportToFile());

        const chatInput = document.getElementById('chat-input') as HTMLTextAreaElement;
        if (chatInput) {
            chatInput.addEventListener('keypress', (e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    this.sendChatMessage();
                }
            });
        }

        // Sprint 39: File attachment listeners
        this.setupFileAttachment();

        // Sprint 39: API key settings listeners
        this.setupApiKeySettings();
    }

    /**
     * Sprint 39: Setup file attachment handlers
     */
    private setupFileAttachment(): void {
        const btnAttach = document.getElementById('btn-attach-file');
        const fileInput = document.getElementById('file-input') as HTMLInputElement;
        const btnRemove = document.getElementById('btn-remove-attachment');

        if (btnAttach && fileInput) {
            btnAttach.addEventListener('click', () => fileInput.click());

            fileInput.addEventListener('change', async (e) => {
                const target = e.target as HTMLInputElement;
                const file = target.files?.[0];
                if (file) {
                    await this.handleFileSelection(file);
                }
                // Reset input so same file can be selected again
                target.value = '';
            });
        }

        if (btnRemove) {
            btnRemove.addEventListener('click', () => this.removeAttachment());
        }
    }

    /**
     * Sprint 39: Handle file selection and upload
     */
    private async handleFileSelection(file: File): Promise<void> {
        const attachmentArea = document.getElementById('attachment-area');
        const attachmentChip = document.getElementById('attachment-chip');
        const uploadProgress = document.getElementById('upload-progress');
        const attachmentName = document.getElementById('attachment-name');
        const attachmentSize = document.getElementById('attachment-size');

        if (!attachmentArea || !attachmentChip || !uploadProgress) return;

        // Show upload progress
        attachmentArea.classList.remove('hidden');
        attachmentChip.classList.add('hidden');
        uploadProgress.classList.remove('hidden');

        try {
            // Upload file to proxy server
            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch(`${environment.proxyUrl}/ada/upload`, {
                method: 'POST',
                body: formData
            });

            const result = await response.json();

            if (result.success) {
                // Store attachment info
                this.attachedFile = {
                    filePath: result.file_path,
                    fileName: result.file_name,
                    fileSize: result.file_size,
                    fileType: result.file_type
                };

                // Update UI
                if (attachmentName) attachmentName.textContent = result.file_name;
                if (attachmentSize) attachmentSize.textContent = `(${this.formatFileSize(result.file_size)})`;

                uploadProgress.classList.add('hidden');
                attachmentChip.classList.remove('hidden');

                console.log('File uploaded:', this.attachedFile);

                if ((window as any).showToastNotification) {
                    (window as any).showToastNotification(`File attached: ${result.file_name}`, 'success');
                }
            } else {
                throw new Error(result.error || 'Upload failed');
            }
        } catch (error) {
            console.error('File upload error:', error);
            attachmentArea.classList.add('hidden');

            if ((window as any).showToastNotification) {
                (window as any).showToastNotification(`Upload failed: ${(error as Error).message}`, 'error');
            }
        }
    }

    /**
     * Sprint 39: Remove attached file
     */
    private removeAttachment(): void {
        this.attachedFile = null;
        const attachmentArea = document.getElementById('attachment-area');
        if (attachmentArea) {
            attachmentArea.classList.add('hidden');
        }
    }

    /**
     * Sprint 39: Format file size for display
     */
    private formatFileSize(bytes: number): string {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }

    /**
     * Sprint 50: Check if a filename is an image type
     */
    private isImageFile(filename: string): boolean {
        const ext = filename.split('.').pop()?.toLowerCase() || '';
        return ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext);
    }

    /**
     * Sprint 50: Send image for vision extraction and knowledge base ingest
     */
    private async sendImageIngestMessage(instructions: string): Promise<void> {
        const input = document.getElementById('chat-input') as HTMLInputElement;
        if (!input || !this.attachedFile) return;

        const apiKey = this.getStoredApiKey();
        if (!apiKey) {
            this.openApiKeyModal();
            if ((window as any).showToastNotification) {
                (window as any).showToastNotification('Please configure your Claude API key first', 'warning');
            }
            return;
        }

        const userMessage = instructions || `Please analyze this image: ${this.attachedFile.fileName}`;
        this.chatManager.addMessage('user', `🖼️ ${this.attachedFile.fileName}\n\n${userMessage}`);
        input.value = '';

        const attachedFilePath = this.attachedFile.filePath;
        const attachedFileName = this.attachedFile.fileName;
        this.removeAttachment();

        const typingId = this.chatManager.addMessage('agent', 'Ada is analyzing your image with Claude Vision...', true);

        try {
            const response = await fetch(`${environment.proxyUrl}/ada/image-ingest`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    file_path: attachedFilePath,
                    instructions: userMessage,
                    api_key: apiKey,
                })
            });

            const result = await response.json();
            this.chatManager.removeMessage(typingId);

            if (result.success) {
                const entities = result.entities?.length ? result.entities.join(', ') : 'none detected';
                const summary = [
                    `**Image Analyzed:** ${attachedFileName}`,
                    `**Type:** ${result.doc_type || 'image'}`,
                    `**Description:** ${result.description || 'No description'}`,
                    `**Entities Found:** ${entities}`,
                    `**Chunks Stored:** ${result.chunks_stored} (added to knowledge base)`,
                    '',
                    'You can now ask me questions about this image and I\'ll use it as context.',
                ].join('\n');
                this.chatManager.addMessage('agent', summary);
            } else {
                this.chatManager.addMessage('agent', `Image analysis failed: ${result.error || 'Unknown error'}`);
            }
        } catch (error) {
            this.chatManager.removeMessage(typingId);
            this.chatManager.addMessage('agent', `Image analysis error: ${(error as Error).message}`);
        }
    }

    /**
     * Sprint 39: Setup API key settings handlers
     */
    private setupApiKeySettings(): void {
        const btnSettings = document.getElementById('btn-api-key-settings');
        const btnToggleVisibility = document.getElementById('btn-toggle-key-visibility');
        const apiKeyInput = document.getElementById('api-key-input') as HTMLInputElement;

        if (btnSettings) {
            btnSettings.addEventListener('click', () => this.openApiKeyModal());
        }

        if (btnToggleVisibility && apiKeyInput) {
            btnToggleVisibility.addEventListener('click', () => {
                apiKeyInput.type = apiKeyInput.type === 'password' ? 'text' : 'password';
            });
        }
    }

    /**
     * Sprint 39: Open API key modal
     */
    public openApiKeyModal(): void {
        const modal = document.getElementById('api-key-modal');
        const apiKeyInput = document.getElementById('api-key-input') as HTMLInputElement;

        if (modal) {
            modal.classList.remove('hidden');

            // Load existing key (masked)
            const existingKey = this.getStoredApiKey();
            if (apiKeyInput && existingKey) {
                apiKeyInput.value = existingKey;
                this.showApiKeyStatus('API key is configured', 'success');
            }
        }
    }

    /**
     * Sprint 39: Close API key modal
     */
    public closeApiKeyModal(): void {
        const modal = document.getElementById('api-key-modal');
        if (modal) {
            modal.classList.add('hidden');
        }
    }

    /**
     * Sprint 39: Save API key
     */
    public saveApiKey(): void {
        const apiKeyInput = document.getElementById('api-key-input') as HTMLInputElement;
        const key = apiKeyInput?.value?.trim();

        if (!key) {
            this.showApiKeyStatus('Please enter an API key', 'error');
            return;
        }

        if (!key.startsWith('sk-ant-')) {
            this.showApiKeyStatus('Invalid API key format. Key should start with sk-ant-', 'error');
            return;
        }

        try {
            localStorage.setItem(this.API_KEY_STORAGE_KEY, key);
            this.showApiKeyStatus('API key saved successfully', 'success');

            if ((window as any).showToastNotification) {
                (window as any).showToastNotification('API key saved', 'success');
            }

            // Close modal after short delay
            setTimeout(() => this.closeApiKeyModal(), 1000);
        } catch (error) {
            this.showApiKeyStatus('Failed to save API key', 'error');
        }
    }

    /**
     * Sprint 39: Clear API key
     */
    public clearApiKey(): void {
        try {
            localStorage.removeItem(this.API_KEY_STORAGE_KEY);
            const apiKeyInput = document.getElementById('api-key-input') as HTMLInputElement;
            if (apiKeyInput) apiKeyInput.value = '';
            this.showApiKeyStatus('API key cleared', 'success');

            if ((window as any).showToastNotification) {
                (window as any).showToastNotification('API key cleared', 'info');
            }
        } catch (error) {
            this.showApiKeyStatus('Failed to clear API key', 'error');
        }
    }

    /**
     * Sprint 39: Get stored API key
     */
    private getStoredApiKey(): string | null {
        try {
            return localStorage.getItem(this.API_KEY_STORAGE_KEY);
        } catch {
            return null;
        }
    }

    /**
     * Sprint 39: Show API key status message
     */
    private showApiKeyStatus(message: string, type: 'success' | 'error'): void {
        const statusDiv = document.getElementById('api-key-status');
        if (statusDiv) {
            statusDiv.classList.remove('hidden', 'bg-green-50', 'text-green-700', 'bg-red-50', 'text-red-700');
            if (type === 'success') {
                statusDiv.classList.add('bg-green-50', 'text-green-700');
            } else {
                statusDiv.classList.add('bg-red-50', 'text-red-700');
            }
            statusDiv.textContent = message;
        }
    }

    /**
     * Load initial data
     */
    async loadInitialData(): Promise<void> {
        if (this.dataLoaded) return;

        try {
            const stats = await (window as any).iiAgent.getStats();
            this.analysisRenderer.displayStats(stats);
        } catch (error) {
            console.error('Error loading initial data:', error);
        }

        this.dataLoaded = true;
    }

    /**
     * Refresh stats
     */
    async refreshStats(): Promise<void> {
        const stats = await (window as any).iiAgent.getStats();
        this.analysisRenderer.displayStats(stats);
    }

    // ========================================================================
    // Analysis Methods - Using AnalysisRenderer
    // ========================================================================

    private async runInfrastructureAnalysis(): Promise<void> {
        const btn = document.getElementById('btn-analyze-infrastructure') as HTMLButtonElement;
        btn.disabled = true;
        btn.innerHTML = '<i data-feather="loader" class="animate-spin"></i> Analyzing...';
        safeFeatherReplace();

        try {
            const report = await (window as any).iiAgent.analyzeInfrastructure() as InfrastructureReport;
            this.analysisRenderer.displayInfrastructureReport(report);
        } catch (error) {
            console.error('Analysis failed:', error);
            if ((window as any).showToastNotification) {
                (window as any).showToastNotification('Analysis failed: ' + (error as Error).message, 'error');
            }
        } finally {
            btn.disabled = false;
            btn.innerHTML = '<i data-feather="bar-chart-2"></i> Analyze Infrastructure';
            safeFeatherReplace();
        }
    }

    private async runAnomalyDetection(): Promise<void> {
        const thresholdInput = document.getElementById('anomaly-threshold') as HTMLInputElement;
        const threshold = parseFloat(thresholdInput?.value || '0.5');
        const btn = document.getElementById('btn-detect-anomalies') as HTMLButtonElement;
        btn.disabled = true;
        btn.innerHTML = '<i data-feather="loader" class="animate-spin"></i> Detecting...';
        safeFeatherReplace();

        try {
            const anomalies = await (window as any).iiAgent.detectAnomalies(threshold) as Anomaly[];
            this.analysisRenderer.displayAnomalies(anomalies);
        } catch (error) {
            console.error('Anomaly detection failed:', error);
        } finally {
            btn.disabled = false;
            btn.innerHTML = '<i data-feather="alert-triangle"></i> Detect Anomalies';
            safeFeatherReplace();
        }
    }

    private async runSecurityAudit(): Promise<void> {
        const btn = document.getElementById('btn-audit-security') as HTMLButtonElement;
        btn.disabled = true;
        btn.innerHTML = '<i data-feather="loader" class="animate-spin"></i> Auditing...';
        safeFeatherReplace();

        try {
            const findings = await (window as any).iiAgent.auditSecurity() as SecurityFinding[];
            this.analysisRenderer.displaySecurityFindings(findings);
        } catch (error) {
            console.error('Security audit failed:', error);
        } finally {
            btn.disabled = false;
            btn.innerHTML = '<i data-feather="shield"></i> Audit Security';
            safeFeatherReplace();
        }
    }

    private async runCostOptimization(): Promise<void> {
        const btn = document.getElementById('btn-cost-optimization') as HTMLButtonElement;
        btn.disabled = true;
        btn.innerHTML = '<i data-feather="loader" class="animate-spin"></i> Analyzing...';
        safeFeatherReplace();

        try {
            const recommendations = await (window as any).iiAgent.recommendCostOptimizations() as CostRecommendation[];
            this.analysisRenderer.displayCostRecommendations(recommendations);
        } catch (error) {
            console.error('Cost optimization failed:', error);
        } finally {
            btn.disabled = false;
            btn.innerHTML = '<i data-feather="dollar-sign"></i> Optimize Costs';
            safeFeatherReplace();
        }
    }

    // ========================================================================
    // Service Map Methods - Using ServiceMapRenderer
    // ========================================================================

    private async generateServiceMap(): Promise<void> {
        const promptInput = document.getElementById('service-map-prompt') as HTMLInputElement;
        const resultsDiv = document.getElementById('service-map-results') as HTMLElement;
        const btn = document.getElementById('btn-generate-service-map') as HTMLButtonElement;

        const prompt = promptInput.value.trim();
        if (!prompt) {
            alert('Please enter a service description or prompt');
            return;
        }

        btn.disabled = true;
        btn.innerHTML = '<i data-feather="loader" class="animate-spin"></i> Generating...';
        safeFeatherReplace();

        try {
            const serviceMap = await (window as any).iiAgent.generateServiceMapLive(prompt) as ServiceMap;
            await this.serviceMapRenderer.displayServiceMap(serviceMap, resultsDiv);
        } catch (error) {
            console.error('Service map generation failed:', error);
            resultsDiv.innerHTML = `
                <div class="alert alert-error">
                    <i data-feather="alert-circle"></i>
                    <span>Error generating service map: ${(error as Error).message}</span>
                </div>
            `;
            resultsDiv.classList.remove('hidden');
            safeFeatherReplace();
        } finally {
            btn.disabled = false;
            btn.innerHTML = '<i data-feather="map"></i> Generate Map';
            safeFeatherReplace();
        }
    }

    // ========================================================================
    // Chat Methods - Using ChatManager and ClaudeService
    // ========================================================================

    private async sendChatMessage(): Promise<void> {
        if (!this.initializationComplete) return;

        const input = document.getElementById('chat-input') as HTMLInputElement;
        if (!input) return;

        const message = input.value.trim();
        if (!message && !this.attachedFile) return;

        // Sprint 39/50: If file is attached, route by type
        if (this.attachedFile) {
            if (this.isImageFile(this.attachedFile.fileName)) {
                await this.sendImageIngestMessage(message);
            } else {
                await this.sendFileImportMessage(message);
            }
            return;
        }

        // Add user message
        this.chatManager.addMessage('user', message);
        input.value = '';

        // Get AI backend setting
        const freshSettings = (window as any).SettingsService ? new (window as any).SettingsService() : null;
        const aiBackend = freshSettings?.getAiBackend() || 'local';

        // Show typing indicator
        let typingMessage = aiBackend === 'claude' ? 'Claude is thinking...' :
                           aiBackend === 'llm' ? 'LLM is processing...' : '...';
        const typingId = this.chatManager.addMessage('agent', typingMessage, true);

        try {
            let responseText: string;
            let responseData: any = null;
            let costEstimate: number | null = null;
            let backendInfo: string | null = null;
            let responseTime: number | null = null;
            let documentSources: DocumentSource[] = [];

            if (aiBackend === 'claude') {
                const result = await this.handleClaudeChat(message);
                responseText = result.responseText;
                this.conversationId = result.conversationId;
                costEstimate = result.costEstimate;
                backendInfo = result.backendInfo;

                if (result.error) {
                    // Fall back to local pattern matching
                    const response = await (window as any).iiAgent.processNaturalLanguageQuery(message);
                    responseText = response.response;
                    responseData = response.data;
                    backendInfo = 'Pattern (fallback)';
                    this.uiStateManager.showErrorBanner(
                        'Claude Error',
                        `Failed to connect to Claude: ${result.error}`,
                        'Using pattern-based responses as fallback.'
                    );
                } else {
                    this.uiStateManager.hideErrorBanner();
                }
            } else if (aiBackend === 'llm') {
                // Check if the active model is actually a Claude model — route accordingly
                const llmSettings = (window as any).SettingsService ? new (window as any).SettingsService() : null;
                const activeModel = llmSettings?.getAiModel() || '';
                if (activeModel.startsWith('claude-')) {
                    // Claude model selected via LLM switcher — use Claude path
                    const result = await this.handleClaudeChat(message);
                    responseText = result.responseText;
                    this.conversationId = result.conversationId;
                    costEstimate = result.costEstimate;
                    backendInfo = result.backendInfo;
                    if (result.error) {
                        const response = await (window as any).iiAgent.processNaturalLanguageQuery(message);
                        responseText = response.response;
                        responseData = response.data;
                        backendInfo = 'Pattern (fallback)';
                    }
                } else {
                    const result = await this.handleLLMChat(message);
                    responseText = result.responseText;
                    this.conversationId = result.conversationId;
                    backendInfo = result.backendInfo;
                    responseTime = result.responseTime;
                }
            } else {
                // Local pattern-based
                const response = await (window as any).iiAgent.processNaturalLanguageQuery(message);
                responseText = response.response;
                responseData = response.data;
            }

            // Remove typing indicator
            this.chatManager.removeMessage(typingId);

            // Add agent response
            this.chatManager.addMessage('agent', responseText, false, responseData);

            // Check for service map visualization
            await this.checkAndOfferServiceMapVisualization(responseText, message);

            // Display document sources if available
            if (documentSources.length > 0) {
                this.chatManager.displayDocumentSources(documentSources, typingId);
            }

            // Show additional info in the message
            if (costEstimate !== null && costEstimate > 0) {
                this.chatManager.appendToLastMessage(`<br><br><em class="text-xs text-gray-500">Query cost: $${costEstimate.toFixed(4)}</em>`);
            }

            if (backendInfo && responseTime !== null) {
                this.chatManager.appendToLastMessage(`<br><em class="text-xs text-gray-500">Backend: ${backendInfo} • Response time: ${responseTime}ms</em>`);
            }

            // Auto-save conversation
            await this.autoSaveConversation();

        } catch (error) {
            this.chatManager.removeMessage(typingId);
            this.chatManager.addMessage('agent', `Error: ${(error as Error).message}`);
        }
    }

    /**
     * Sprint 39: Send message with file attachment for import
     */
    private async sendFileImportMessage(instructions: string): Promise<void> {
        const input = document.getElementById('chat-input') as HTMLInputElement;
        if (!input || !this.attachedFile) return;

        // Check for API key
        const apiKey = this.getStoredApiKey();
        if (!apiKey) {
            this.openApiKeyModal();
            if ((window as any).showToastNotification) {
                (window as any).showToastNotification('Please configure your Claude API key first', 'warning');
            }
            return;
        }

        const userMessage = instructions || `Please analyze and import this file: ${this.attachedFile.fileName}`;

        // Add user message with file info
        this.chatManager.addMessage('user', `📎 ${this.attachedFile.fileName}\n\n${userMessage}`);
        input.value = '';

        // Clear attachment
        const attachedFilePath = this.attachedFile.filePath;
        const attachedFileName = this.attachedFile.fileName;
        this.removeAttachment();

        // Show typing indicator
        const typingId = this.chatManager.addMessage('agent', 'Ada is analyzing your file...', true);

        try {
            // Call import endpoint with SSE streaming to survive Cloudflare timeouts
            const response = await fetch(`${environment.proxyUrl}/ada/import`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'text/event-stream',
                },
                body: JSON.stringify({
                    file_path: attachedFilePath,
                    instructions: userMessage,
                    api_key: apiKey,
                    backend: 'claude',
                    max_turns: 30
                })
            });

            // Parse SSE stream — collect final result (Sprint 52: deterministic pipeline events)
            let result: any = null;
            const reader = response.body?.getReader();
            const decoder = new TextDecoder();
            if (reader) {
                let buffer = '';
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    buffer += decoder.decode(value, { stream: true });
                    // Process complete SSE lines
                    const lines = buffer.split('\n');
                    buffer = lines.pop() || '';
                    for (const line of lines) {
                        if (!line.startsWith('data: ')) continue;
                        const payload = line.slice(6).trim();
                        if (payload === '[DONE]') continue;
                        try {
                            const evt = JSON.parse(payload);
                            if (evt.error) {
                                result = { Err: evt.error };
                            } else if (evt.Ok) {
                                result = evt;
                            } else if (evt.phase) {
                                // Sprint 52: Deterministic pipeline progress events
                                const typingEl = document.getElementById(typingId);
                                if (typingEl) {
                                    const textEl = typingEl.querySelector('.message-text, p') || typingEl;
                                    if (evt.phase === 'mapping') {
                                        textEl.textContent = `Analyzing spreadsheet structure... ${evt.sheets} sheets, ${evt.rows} rows`;
                                    } else if (evt.phase === 'importing') {
                                        textEl.textContent = `Importing sheet ${evt.sheet_index}/${evt.total_sheets}: ${evt.sheet} (${evt.rows} rows)...`;
                                    } else if (evt.phase === 'row_progress') {
                                        textEl.textContent = `Importing ${evt.sheet}: row ${evt.row}/${evt.total} (${evt.imported} saved)...`;
                                    } else if (evt.phase === 'sheet_done') {
                                        textEl.textContent = `Sheet ${evt.sheet_index}/${evt.total_sheets} done: ${evt.imported}/${evt.total} imported` +
                                            (evt.errors > 0 ? ` (${evt.errors} errors)` : '');
                                    }
                                }
                            } else if (evt.turn && evt.status) {
                                // Legacy: AI agent loop progress (kept for backwards compat)
                                const typingEl = document.getElementById(typingId);
                                if (typingEl) {
                                    const toolInfo = evt.tool ? ` (${evt.tool})` : '';
                                    const textEl = typingEl.querySelector('.message-text, p') || typingEl;
                                    textEl.textContent = `Ada is analyzing your file... Turn ${evt.turn}${toolInfo}`;
                                }
                            }
                        } catch { /* skip malformed lines */ }
                    }
                }
            }
            if (!result) {
                // Fallback: try parsing as regular JSON (non-streaming)
                result = await response.json().catch(() => ({ Err: 'Import failed: no response received' }));
            }

            // Remove typing indicator
            this.chatManager.removeMessage(typingId);

            if (result.Ok) {
                // Format and display the import results
                const formattedResponse = this.formatImportResults(result.Ok, attachedFileName);
                this.chatManager.addMessage('agent', formattedResponse);

                // Sprint 42: Show "View in Constellation" button if session has entities
                const session = result.Ok.session;
                if (session && session.session_id && session.entity_counts
                    && Object.keys(session.entity_counts).length > 0) {
                    this.renderConstellationButton(session.session_id);
                }

                if ((window as any).showToastNotification) {
                    (window as any).showToastNotification('File import completed', 'success');
                }
            } else if (result.Err) {
                this.chatManager.addMessage('agent', `❌ Import failed: ${result.Err}`);
            } else {
                this.chatManager.addMessage('agent', '❌ Import failed: Unknown error');
            }

            // Auto-save conversation
            await this.autoSaveConversation();

        } catch (error) {
            this.chatManager.removeMessage(typingId);
            this.chatManager.addMessage('agent', `❌ Import error: ${(error as Error).message}`);
        }
    }

    /**
     * Sprint 42: Format import results with session summary card
     */
    private formatImportResults(result: any, fileName: string): string {
        let response = `## 📊 Import Results for ${fileName}\n\n`;
        response += result.message || 'Import completed.';

        // Add session summary card (Sprint 42)
        const session = result.session;
        if (session && session.entity_counts) {
            response += '\n\n---\n### 📋 Import Session Summary\n';
            response += `**Session ID:** \`${session.session_id}\`\n`;
            response += `**Document:** ${session.filename}\n`;
            response += `**Started:** ${new Date(session.started_at).toLocaleString()}\n\n`;

            // Entity counts table
            const counts = session.entity_counts;
            const totalEntities = Object.values(counts).reduce((sum: number, c: any) => sum + (c as number), 0);
            if (totalEntities > 0) {
                response += '| Entity Type | Count |\n|---|---|\n';
                for (const [entityType, count] of Object.entries(counts)) {
                    response += `| ${entityType} | ${count} |\n`;
                }
                response += `| **Total** | **${totalEntities}** |\n`;
            }

            if (session.relationship_count > 0) {
                response += `\n**Relationships created:** ${session.relationship_count}\n`;
            }

            if (session.errors && session.errors.length > 0) {
                response += `\n⚠️ **Errors:** ${session.errors.length}\n`;
            }
        }

        // Add tool call summary if available
        if (result.tool_calls && result.tool_calls.length > 0) {
            const importCalls = result.tool_calls.filter(
                (tc: any) => tc.tool === 'import_entities' || tc.tool === 'create_relationships'
            );
            if (importCalls.length > 0) {
                response += '\n\n### 🔧 Operations Performed\n';
                for (const tc of importCalls) {
                    if (tc.result_summary) {
                        response += `- **${tc.tool}**: ${tc.result_summary}\n`;
                    }
                }
            }
        }

        return response;
    }

    /**
     * Sprint 42: Render "View in Constellation" button in chat
     */
    private renderConstellationButton(sessionId: string): void {
        const chatMessages = document.getElementById('chat-messages');
        if (!chatMessages) return;

        const buttonDiv = document.createElement('div');
        buttonDiv.className = 'flex gap-2 mt-3 mb-2';
        buttonDiv.innerHTML = `
            <button onclick="window.viewImportConstellation && window.viewImportConstellation('${sessionId}')"
                class="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="3" fill="currentColor"/>
                    <path d="M12 2v4M12 18v4M2 12h4M18 12h4" stroke-linecap="round" stroke-width="2"/>
                    <circle cx="12" cy="12" r="8" stroke-dasharray="2 3" stroke-width="1.5"/>
                </svg>
                View in Constellation
            </button>
            <button onclick="window.showImportHistory && window.showImportHistory()"
                class="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                Import History
            </button>
        `;
        chatMessages.appendChild(buttonDiv);
        chatMessages.scrollTop = chatMessages.scrollHeight;
    }

    /**
     * Handle Claude AI chat
     */
    private async handleClaudeChat(message: string): Promise<{
        responseText: string;
        conversationId: string | null;
        costEstimate: number | null;
        backendInfo: string | null;
        error: string | null;
    }> {
        const canisterClient = (window as any).canisterClient;
        const infrastructureContext = await this.contextService.getInfrastructureContext();

        // Build conversation history
        const conversationHistory = this.chatManager.getMessagesWithoutTyping()
            .slice(0, -1)
            .map(m => ({
                role: m.sender === 'user' ? 'user' : 'assistant',
                content: m.content
            }));

        const systemContext = this.buildSystemContext(infrastructureContext);

        const messages = [
            { role: 'user', content: systemContext },
            { role: 'assistant', content: 'I understand. I have access to your infrastructure data. How can I help you?' },
            ...conversationHistory,
            { role: 'user', content: message }
        ];

        try {
            if (isMainnet()) {
                // Frontend-assisted mode
                const apiKeyResult = await canisterClient.getApiKeyForFrontend();
                if (!('Ok' in apiKeyResult)) {
                    throw new Error(apiKeyResult.Err || 'Failed to get API key');
                }

                const claudeResult = await this.claudeService.chatDirect(messages, apiKeyResult.Ok, systemContext);

                // Track response in canister
                const trackingRequest = {
                    messages: messages.map(m => ({ role: m.role, content: m.content })),
                    context: [],                                    // Candid opt text: [] for None
                    conversation_id: this.conversationId ? [this.conversationId] : [],  // [value] for Some, [] for None
                    claude_response: claudeResult.message,
                    input_tokens: claudeResult.inputTokens,
                    output_tokens: claudeResult.outputTokens
                };

                const trackingResult = await canisterClient.chatFrontendAssisted(trackingRequest);

                if ('Ok' in trackingResult) {
                    return {
                        responseText: this.chatManager.unescapeContent(claudeResult.message),
                        conversationId: trackingResult.Ok.conversation_id,
                        costEstimate: trackingResult.Ok.cost_estimate,
                        backendInfo: 'Claude AI (Frontend-Assisted)',
                        error: null
                    };
                } else {
                    return {
                        responseText: this.chatManager.unescapeContent(claudeResult.message),
                        conversationId: claudeResult.conversationId,
                        costEstimate: null,
                        backendInfo: 'Claude AI (Untracked)',
                        error: null
                    };
                }
            } else {
                // Local development - canister HTTP outcall
                const chatRequest = {
                    messages,
                    context: [],                                    // Candid opt text: [] for None
                    conversation_id: this.conversationId ? [this.conversationId] : []  // [value] for Some, [] for None
                };

                const result = await canisterClient.chatWithClaude(chatRequest);

                if ('Ok' in result) {
                    return {
                        responseText: this.chatManager.unescapeContent(result.Ok.message),
                        conversationId: result.Ok.conversation_id,
                        costEstimate: result.Ok.cost_estimate,
                        backendInfo: 'Claude AI',
                        error: null
                    };
                } else {
                    throw new Error(result.Err || 'Claude API request failed');
                }
            }
        } catch (error) {
            return {
                responseText: '',
                conversationId: null,
                costEstimate: null,
                backendInfo: null,
                error: (error as Error).message
            };
        }
    }

    /**
     * Handle self-hosted LLM chat
     */
    private async handleLLMChat(message: string): Promise<{
        responseText: string;
        conversationId: string | null;
        backendInfo: string | null;
        responseTime: number | null;
    }> {
        const infrastructureContext = await this.contextService.getInfrastructureContext();

        const contextualMessage = `Here is my infrastructure data:\n${infrastructureContext}\n\nBased on this data, ${message}`;

        const conversationHistory = this.chatManager.getMessagesWithoutTyping()
            .slice(0, -1)
            .map(m => ({
                role: m.sender === 'user' ? 'user' : 'assistant',
                content: m.content
            }));

        const messages = [
            ...conversationHistory,
            { role: 'user', content: contextualMessage }
        ];

        const { ollamaChat } = await import('../api/proxy-services');
        const { SettingsService } = await import('../utils/settings-service');
        const activeModel = new SettingsService().getAiModel();
        const chatResult = await ollamaChat(messages, this.conversationId, activeModel);

        return {
            responseText: chatResult.message,
            conversationId: chatResult.conversation_id,
            backendInfo: chatResult.backend_used,
            responseTime: chatResult.response_time_ms
        };
    }

    /**
     * Build system context for Claude
     */
    private buildSystemContext(infrastructureContext: string): string {
        return `You are Ada, an AI infrastructure assistant for the ASD (Agentic Service Discovery) system. You have access to the following infrastructure data. Use this to answer questions naturally and conversationally.

${infrastructureContext}

When the user asks about devices, services, or infrastructure, refer to this data. Be helpful, concise, and conversational.`;
    }

    /**
     * Check if response mentions a service map and offer visualization
     */
    private async checkAndOfferServiceMapVisualization(responseText: string, userQuery: string): Promise<void> {
        try {
            const canisterClient = (window as any).canisterClient;
            if (!canisterClient) return;

            const serviceMapNames = await canisterClient.callCanister('vector_db', 'list_service_map_names', []);
            if (!serviceMapNames || serviceMapNames.length === 0) return;

            const lowerResponse = responseText.toLowerCase();
            const lowerQuery = userQuery.toLowerCase();

            const visualizationKeywords = [
                'show me', 'display', 'visualize', 'view', 'see the',
                'look at', 'open', 'what does', 'architecture', 'topology',
                'service map', 'diagram'
            ];

            const wantsVisualization = visualizationKeywords.some(kw =>
                lowerQuery.includes(kw) || lowerResponse.includes(kw)
            );

            const matchedMaps: string[] = [];
            for (const mapName of serviceMapNames) {
                const lowerMapName = mapName.toLowerCase();
                if (lowerResponse.includes(lowerMapName) ||
                    lowerQuery.includes(lowerMapName) ||
                    lowerMapName.split(' ').some((word: string) =>
                        word.length > 3 && (lowerResponse.includes(word) || lowerQuery.includes(word))
                    )) {
                    matchedMaps.push(mapName);
                }
            }

            if (matchedMaps.length > 0) {
                const chatMessages = document.getElementById('chat-messages');
                const lastMessage = chatMessages?.lastElementChild;

                if (lastMessage) {
                    const bubble = lastMessage.querySelector('.inline-block');
                    if (bubble) {
                        const buttonContainer = document.createElement('div');
                        buttonContainer.className = 'mt-3 pt-3 border-t border-gray-200 flex flex-wrap gap-2';

                        for (const mapName of matchedMaps) {
                            const button = document.createElement('button');
                            button.className = 'inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition-colors';
                            button.innerHTML = `
                                <svg class="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                          d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"/>
                                </svg>
                                View ${mapName}
                            `;
                            button.onclick = () => this.openServiceMapVisualization(mapName);
                            buttonContainer.appendChild(button);
                        }

                        bubble.appendChild(buttonContainer);

                        if (wantsVisualization && matchedMaps.length === 1) {
                            setTimeout(() => this.openServiceMapVisualization(matchedMaps[0]), 500);
                        }
                    }
                }
            }
        } catch (error) {
            console.error('Error checking for service map mention:', error);
        }
    }

    /**
     * Open service map visualization modal
     */
    private openServiceMapVisualization(serviceName: string): void {
        const controller = (window as any).serviceMapController;
        if (controller && typeof controller.loadServiceMap === 'function') {
            controller.loadServiceMap(serviceName);
        } else {
            const modal = document.getElementById('service-map-modal');
            if (modal) {
                modal.classList.remove('hidden');
                if (typeof (window as any).visualizeServiceMap === 'function') {
                    setTimeout(() => (window as any).visualizeServiceMap(serviceName), 100);
                }
            } else if (typeof (window as any).showPage === 'function') {
                (window as any).showPage('service-models');
            }
        }
    }

    // ========================================================================
    // View and History Methods
    // ========================================================================

    /**
     * Switch view
     */
    switchView(view: ViewType): void {
        this.uiStateManager.switchView(view);
    }

    /**
     * Load conversation from header Ask Ada
     */
    public async loadConversationFromHeader(question: string, answer: string): Promise<void> {
        this.switchView('chat');

        this.chatManager.clearMessages();
        this.currentChatId = null;
        this.conversationId = null;

        this.chatManager.addMessage('user', question);
        this.chatManager.addMessage('agent', answer);

        await this.autoSaveConversation();
    }

    /**
     * Load chat history
     */
    public async loadChatHistory(): Promise<void> {
        await this.historyManager.load();
    }

    /**
     * Search history
     */
    public searchHistory(query: string): void {
        this.historyManager.search(query);
    }

    /**
     * Start new chat
     */
    public startNewChat(): void {
        this.currentChatId = null;
        this.conversationId = null;
        this.chatManager.clearMessages();
        this.switchView('chat');

        const chatInput = document.getElementById('chat-input') as HTMLTextAreaElement;
        if (chatInput) chatInput.focus();

        if ((window as any).showToastNotification) {
            (window as any).showToastNotification('New chat started', 'info');
        }
    }

    /**
     * Load a specific history entry
     */
    public async loadHistoryEntry(chatId: string): Promise<void> {
        try {
            const entry = await this.historyManager.loadEntry(chatId);

            if (!entry) {
                if ((window as any).showToastNotification) {
                    (window as any).showToastNotification('Conversation not found', 'error');
                }
                return;
            }

            this.currentChatId = entry.id;
            this.chatManager.loadMessages(entry.messages);
            this.switchView('chat');

            if ((window as any).showToastNotification) {
                (window as any).showToastNotification('Conversation loaded', 'success');
            }

        } catch (error) {
            console.error('Failed to load history entry:', error);
            if ((window as any).showToastNotification) {
                (window as any).showToastNotification('Failed to load conversation', 'error');
            }
        }
    }

    /**
     * Delete history entry
     */
    public async deleteHistoryEntry(chatId: string): Promise<void> {
        if (!confirm('Are you sure you want to delete this conversation? This cannot be undone.')) {
            return;
        }

        try {
            await this.historyManager.deleteEntry(chatId);

            if (this.currentChatId === chatId) {
                this.startNewChat();
            }

            if ((window as any).showToastNotification) {
                (window as any).showToastNotification('Conversation deleted', 'success');
            }
        } catch (error) {
            console.error('Failed to delete history entry:', error);
            if ((window as any).showToastNotification) {
                (window as any).showToastNotification('Failed to delete conversation', 'error');
            }
        }
    }

    /**
     * Rename history entry
     */
    public async renameHistoryEntry(chatId: string): Promise<void> {
        try {
            const entry = await this.historyManager.loadEntry(chatId);
            if (!entry) {
                if ((window as any).showToastNotification) {
                    (window as any).showToastNotification('Conversation not found', 'error');
                }
                return;
            }

            const newTitle = prompt('Enter a new title for this conversation:', entry.title);
            if (newTitle === null) return;

            if (newTitle.trim() === '') {
                if ((window as any).showToastNotification) {
                    (window as any).showToastNotification('Title cannot be empty', 'warning');
                }
                return;
            }

            await this.historyManager.renameEntry(chatId, newTitle.trim());

            if ((window as any).showToastNotification) {
                (window as any).showToastNotification('Conversation renamed', 'success');
            }
        } catch (error) {
            console.error('Failed to rename history entry:', error);
            if ((window as any).showToastNotification) {
                (window as any).showToastNotification('Failed to rename conversation', 'error');
            }
        }
    }

    /**
     * Navigate history pages
     */
    public prevHistoryPage(): void {
        this.historyManager.prevPage();
    }

    public nextHistoryPage(): void {
        this.historyManager.nextPage();
    }

    /**
     * Auto-save the current conversation
     */
    private async autoSaveConversation(): Promise<void> {
        if (!this.autoSaveEnabled) return;

        const messages = this.chatManager.getMessagesWithoutTyping();
        if (messages.length < 2) return;

        try {
            let title: string | undefined;
            if (!this.currentChatId) {
                const firstUserMsg = messages.find(m => m.sender === 'user');
                const firstAssistantMsg = messages.find(m => m.sender === 'agent');
                if (firstUserMsg && firstAssistantMsg) {
                    title = await this.historyManager.generateSummary(firstUserMsg.content, firstAssistantMsg.content);
                } else if (firstUserMsg) {
                    title = firstUserMsg.content.substring(0, 40) + (firstUserMsg.content.length > 40 ? '...' : '');
                }
            }

            const entry = await this.historyManager.saveConversation(messages, this.currentChatId || undefined, title);
            this.currentChatId = entry.id;

            await this.loadChatHistory();
        } catch (error) {
            console.error('Failed to auto-save conversation:', error);
        }
    }

    /**
     * Clear chat (public for window access)
     */
    public clearChat(): void {
        this.currentChatId = null;
        this.conversationId = null;
        this.chatManager.clearMessages();

        if ((window as any).showToastNotification) {
            (window as any).showToastNotification('Chat cleared', 'info');
        }
    }

    /**
     * Display stats (public for external calls)
     */
    displayStats(stats: any[] | any): void {
        this.analysisRenderer.displayStats(stats);
    }

    /**
     * Display infrastructure report (public for external calls)
     */
    displayInfrastructureReport(report: InfrastructureReport): void {
        this.analysisRenderer.displayInfrastructureReport(report);
    }

    /**
     * Display anomalies (public for external calls)
     */
    displayAnomalies(anomalies: Anomaly[]): void {
        this.analysisRenderer.displayAnomalies(anomalies);
    }

    /**
     * Display security findings (public for external calls)
     */
    displaySecurityFindings(findings: SecurityFinding[]): void {
        this.analysisRenderer.displaySecurityFindings(findings);
    }

    /**
     * Display cost recommendations (public for external calls)
     */
    displayCostRecommendations(recommendations: CostRecommendation[]): void {
        this.analysisRenderer.displayCostRecommendations(recommendations);
    }
}

// Global instance
declare global {
    interface Window {
        iiAgentUI: IIAgentUI;
        viewImportConstellation: (sessionId: string) => void;
        showImportHistory: () => void;
    }
}

window.iiAgentUI = new IIAgentUI();

// Sprint 42: Global handlers for constellation and import history buttons
window.viewImportConstellation = (sessionId: string) => {
    // Store session ID for the service map controller to pick up
    (window as any).pendingConstellationSessionId = sessionId;

    // Sprint 49: Navigate to Service Map page (fullview constellation)
    const pageManagement = (window as any).pageManagement;
    if (pageManagement && pageManagement.showPage) {
        pageManagement.showPage('service-map');
        // Set session filter dropdown after page renders
        setTimeout(() => {
            const select = document.getElementById('smap-session-filter') as HTMLSelectElement;
            if (select && sessionId) {
                // If the option exists, select it; otherwise the controller will load all data
                for (const opt of Array.from(select.options)) {
                    if (opt.value === sessionId) {
                        select.value = sessionId;
                        select.dispatchEvent(new Event('change'));
                        break;
                    }
                }
            }
        }, 500);
    }
};

window.showImportHistory = async () => {
    try {
        const response = await fetch(`${environment.proxyUrl}/ada/import-sessions`);
        const data = await response.json();

        if (!data.success || !data.sessions || data.sessions.length === 0) {
            window.iiAgentUI.chatManager?.addMessage('agent',
                '📋 No import sessions found yet. Upload a file to create one.');
            return;
        }

        let historyMsg = '## 📋 Import History\n\n';
        historyMsg += '| Session | Document | Entities | Relationships | Date |\n';
        historyMsg += '|---|---|---|---|---|\n';

        for (const s of data.sessions.slice(0, 10)) {
            const totalEntities = s.entity_counts
                ? Object.values(s.entity_counts).reduce((sum: number, c: any) => sum + (c as number), 0)
                : 0;
            const date = new Date(s.started_at).toLocaleDateString();
            historyMsg += `| \`${s.session_id}\` | ${s.filename} | ${totalEntities} | ${s.relationship_count || 0} | ${date} |\n`;
        }

        window.iiAgentUI.chatManager?.addMessage('agent', historyMsg);

    } catch (error) {
        window.iiAgentUI.chatManager?.addMessage('agent',
            `❌ Failed to load import history: ${(error as Error).message}`);
    }
};
